import { FC, ReactElement, useCallback, useMemo } from "react";
import { Dayjs } from "dayjs";
import { Typography, useTheme } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  InputOnChange,
  MultiselectOnChange,
  StepContentData,
  StepFormFieldProps,
} from "interfaces/sign-up";

import { Input } from "components/common/inputs/Input";
import { InputPhone } from "components/common/inputs/InputPhone";
import { Select } from "components/common/inputs/Select";
import { Multiselect } from "components/common/inputs/Multiselect";
import { DatePicker } from "components/common/DatePicker";
import {
  StyledSignUpFields,
  StyledSignUpHeadings,
  StyledStep,
} from "styles/sign-up";

const Step: FC<StepContentData> = ({
  text,
  title,
  withImgUploader,
  avatar,
  onAvatarChange,
  fields,
}) => {
  const theme = useTheme();

  const renderedImgUploader = useMemo(
    () =>
      withImgUploader ? (
        <div className="step-uploader">Img uploader</div>
      ) : null,
    [withImgUploader]
  );

  const renderStepFormField = useCallback(
    (fieldProps: StepFormFieldProps): ReactElement => {
      const {
        id,
        type,
        value,
        placeholder,
        label,
        error,
        errorText,
        disabled,
        onBlur,
        onChange,
        onFocus,
        options,
        required,
      } = fieldProps;

      switch (type) {
        case "textarea":
          return (
            <Input
              id={id}
              multiline
              rows={4}
              value={value as string}
              onBlur={onBlur}
              onChange={onChange as InputOnChange}
              placeholder={placeholder}
              label={label}
              error={error}
              errorText={errorText}
              disabled={disabled}
              key={label}
              required={required}
            />
          );

        case "phone":
          return (
            <InputPhone
              value={value as string}
              onBlur={onBlur}
              onChange={onChange as InputOnChange}
              onFocus={onFocus}
              placeholder={placeholder}
              label={label}
              error={error}
              errorText={errorText}
              disabled={disabled}
              key={label}
              required={required}
            />
          );

        case "select":
          return (
            <Select
              id={id}
              value={value as string}
              onChange={onChange as InputOnChange}
              options={options}
              placeholder={placeholder}
              label={label}
              error={error}
              errorText={errorText}
              disabled={disabled}
              color="grey"
              key={label}
              required={required}
            />
          );

        case "multiselect":
          return (
            <Multiselect
              id={id as string}
              value={value as string[]}
              onChange={onChange as MultiselectOnChange}
              options={options}
              placeholder={placeholder}
              label={label}
              error={error}
              errorText={errorText}
              disabled={disabled}
              color="grey"
              key={label}
              required={required}
            />
          );
        case "date":
          return (
            <DatePicker
              value={value as Dayjs}
              onChange={onChange as MultiselectOnChange}
              placeholder={placeholder}
              label={label}
              error={error}
              errorText={errorText}
              disabled={disabled}
              key={label}
              disableFuture
            />
          );

        default:
          return (
            <Input
              id={id}
              value={value as string}
              onBlur={onBlur}
              onChange={onChange as InputOnChange}
              placeholder={placeholder}
              label={label}
              error={error}
              errorText={errorText}
              disabled={disabled}
              key={label}
              required={required}
            />
          );
      }
    },
    []
  );

  return (
    <StyledStep>
      <StyledSignUpHeadings>
        <Typography variant="h1" color={theme.palette.common.black}>
          {title}
        </Typography>
        <Typography
          variant="bodyL"
          color={theme.palette.common.black}
          paragraph
          fontWeight={300}
          marginBottom={0}
        >
          {text}
        </Typography>
      </StyledSignUpHeadings>
      {renderedImgUploader}
      <StyledSignUpFields>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
          {fields?.map((field) => renderStepFormField(field))}
        </LocalizationProvider>
      </StyledSignUpFields>
    </StyledStep>
  );
};

export default Step;
